<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import infoIcon from '~/assets/images/enterprise/icons/info.svg'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type PurchaseHelpType = 'plan' | 'fuel'

const props = defineProps<{
  kind: PurchaseHelpType
  globalPlan: boolean
  active: boolean
  pinned: boolean
}>()

const emit = defineEmits<{
  (event: 'open', kind: PurchaseHelpType): void
  (event: 'keep', kind: PurchaseHelpType): void
  (event: 'leave', kind: PurchaseHelpType): void
  (event: 'toggle', kind: PurchaseHelpType): void
}>()

const { locale } = useI18n()
const trigger = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const tooltipContent = ref<HTMLElement>()
const tooltipId = `enterprise-purchase-help-${useId().replaceAll(':', '')}`
const ready = ref(false)
const position = ref({
  top: 0,
  left: 0,
  arrowLeft: 24,
  maxHeight: 480,
  placement: 'below' as 'above' | 'below',
})

const rtl = computed(() => String(locale.value).toLowerCase().startsWith('ar'))
const titleKey = computed(() => props.kind === 'plan'
  ? 'enterprise_ui_purchase_plan_help_title'
  : 'enterprise_ui_purchase_fuel_help_title')
const planRuleKeys = computed(() => props.globalPlan
  ? [
      'enterprise_ui_purchase_plan_global_rule_1',
      'enterprise_ui_purchase_plan_global_rule_2',
      'enterprise_ui_purchase_plan_global_rule_3',
    ]
  : [
      'enterprise_ui_purchase_plan_local_rule_1',
      'enterprise_ui_purchase_plan_local_rule_2',
      'enterprise_ui_purchase_plan_local_rule_3',
    ])

async function updatePosition() {
  if (!props.active || !trigger.value) return
  ready.value = false
  await nextTick()
  if (!tooltip.value || !tooltipContent.value) return

  const rect = trigger.value.getBoundingClientRect()
  const width = tooltip.value.offsetWidth || 400
  const viewportPadding = 12
  const gap = 8
  const anchorCenter = rect.left + rect.width / 2
  const left = Math.min(
    Math.max(anchorCenter - width / 2, viewportPadding),
    Math.max(viewportPadding, window.innerWidth - viewportPadding - width),
  )
  const fullHeight = tooltipContent.value.scrollHeight
  const availableBelow = Math.max(0, window.innerHeight - rect.bottom - gap - viewportPadding - 2)
  const availableAbove = Math.max(0, rect.top - gap - viewportPadding - 2)
  const placement = availableBelow < Math.min(fullHeight, 240) && availableAbove > availableBelow ? 'above' : 'below'
  const availableHeight = placement === 'above' ? availableAbove : availableBelow
  const maxHeight = Math.max(1, Math.floor(availableHeight))
  const renderedHeight = Math.min(fullHeight, maxHeight)

  position.value = {
    top: placement === 'above'
      ? Math.max(viewportPadding, rect.top - gap - renderedHeight - 2)
      : rect.bottom + gap,
    left,
    arrowLeft: Math.min(Math.max(anchorCenter - left - 7, 18), width - 32),
    maxHeight,
    placement,
  }
  ready.value = true
}

function onViewportScroll(event: Event) {
  if (event.target instanceof Node && tooltip.value?.contains(event.target)) return
  void updatePosition()
}

watch(
  [() => props.active, () => props.globalPlan, () => locale.value],
  ([active]) => {
    if (active) void updatePosition()
    else ready.value = false
  },
  { flush: 'post' },
)

onMounted(() => {
  window.addEventListener('resize', updatePosition)
  window.visualViewport?.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', onViewportScroll, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition)
  window.visualViewport?.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', onViewportScroll, true)
})
</script>

<template>
  <span
    class="purchase-help"
    data-purchase-help
    :class="{ active }"
    @mouseenter="emit('open', kind)"
    @mouseleave="emit('leave', kind)"
  >
    <button
      ref="trigger"
      class="purchase-help__trigger"
      type="button"
      :aria-label="$t(titleKey)"
      aria-haspopup="dialog"
      :aria-expanded="active"
      :aria-pressed="pinned"
      :aria-controls="active ? tooltipId : undefined"
      @focus="emit('keep', kind)"
      @blur="emit('leave', kind)"
      @click.stop="emit('toggle', kind)"
    >
      <EnterpriseIcon :src="infoIcon" />
    </button>

    <Teleport to="body">
      <section
        v-if="active"
        :id="tooltipId"
        ref="tooltip"
        class="purchase-help-tooltip"
        data-purchase-help
        :class="{ 'is-above': position.placement === 'above' }"
        :dir="rtl ? 'rtl' : 'ltr'"
        role="dialog"
        :aria-label="$t(titleKey)"
        :style="{
          top: `${position.top}px`,
          left: `${position.left}px`,
          visibility: ready ? 'visible' : 'hidden',
          '--purchase-help-arrow-left': `${position.arrowLeft}px`,
          '--purchase-help-max-height': `${position.maxHeight}px`,
        }"
        @mouseenter="emit('keep', kind)"
        @mouseleave="emit('leave', kind)"
        @wheel.stop
        @touchmove.stop
      >
        <div ref="tooltipContent" class="purchase-help-tooltip__content">
          <strong class="purchase-help-tooltip__title">{{ $t(titleKey) }}</strong>

          <template v-if="kind === 'plan'">
            <span v-for="key in planRuleKeys" :key="key" class="purchase-help-tooltip__rule">
              <i />
              <span>{{ $t(key) }}</span>
            </span>
          </template>

          <template v-else>
            <span class="purchase-help-tooltip__warning">
              {{ $t('enterprise_ui_purchase_fuel_help_freeze') }}
            </span>
            <span class="purchase-help-tooltip__body">
              {{ $t('enterprise_ui_purchase_fuel_help_restore') }}
            </span>
          </template>
        </div>
      </section>
    </Teleport>
  </span>
</template>

<style scoped>
.purchase-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.purchase-help__trigger {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #98a2b3;
  background: transparent;
  cursor: pointer;
}

.purchase-help__trigger .enterprise-svg-icon {
  width: 14px;
  height: 14px;
}

.purchase-help.active .purchase-help__trigger,
.purchase-help__trigger:hover,
.purchase-help__trigger:focus-visible {
  color: #2b7de9;
  background: #eaf3fe;
  outline: none;
}

.purchase-help-tooltip {
  position: fixed;
  z-index: 3300;
  width: 400px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  border: 1px solid #ebeef2;
  border-radius: 12px;
  color: #51565d;
  background: #fff;
  box-shadow: 0 10px 30px #0a234d2e;
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  white-space: normal;
}

.purchase-help-tooltip__content {
  display: flex;
  max-height: var(--purchase-help-max-height, calc(100vh - 24px));
  box-sizing: border-box;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 11px;
  scrollbar-width: thin;
  scrollbar-color: #cbd2dc transparent;
}

.purchase-help-tooltip__content::-webkit-scrollbar { width: 6px; }
.purchase-help-tooltip__content::-webkit-scrollbar-thumb { border-radius: 6px; background: #cbd2dc; }

.purchase-help-tooltip::before {
  position: absolute;
  top: -7px;
  left: var(--purchase-help-arrow-left);
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: solid #ebeef2;
  border-width: 1px 0 0 1px;
  background: #fff;
  content: '';
  transform: rotate(45deg);
}

.purchase-help-tooltip.is-above::before {
  top: auto;
  bottom: -7px;
  border-width: 0 1px 1px 0;
}

.purchase-help-tooltip__title {
  color: #1f2329;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
}

.purchase-help-tooltip__rule {
  display: grid;
  grid-template-columns: 5px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.purchase-help-tooltip__rule i {
  width: 5px;
  height: 5px;
  margin-top: 7px;
  border-radius: 50%;
  background: #98a2b3;
}

.purchase-help-tooltip__warning {
  padding: 9px 12px;
  border-radius: 8px;
  color: #92400e;
  background: #fff4e5;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.6;
}

.purchase-help-tooltip__body {
  color: #51565d;
  font-size: 12px;
  line-height: 1.6;
}
</style>
